import React, { useEffect, Fragment } from 'react';
import T from 'prop-types';
// redux
import { setTableParams } from 'reducers/tableParams';
import { connect } from 'react-redux';
import { openModal } from 'reducers/modals';
import { setIsSchedulePageOutdated } from 'reducers/layout';
import { createUserFilterAction, updateUserFilterAction } from 'reducers/userFilters';
// components
import AcceptCommitmentModal from 'components/CommitmentModal/AcceptCommitmentModal';
import { FormattedMessage, useIntl } from 'react-intl';
import Preloader from 'components/Preloader';
import PaginationWrapper from 'components/PaginationWrapper';
import NothingBox from 'components/NothingBox';
import CommitmentCard from 'components/CommitmentCard/CommitmentCard';
import Panel from 'components/Panel';
import DeclineCommitmentModal from 'components/CommitmentModal/DeclineCommitmentModal';
import DateLine from 'components/DateLine/DateLine';
import OutdatedScheduleModal from './OutdatedScheduleModal';
import ScheduleViewShell from './ScheduleViewShell';
import Button from 'components/Button/Button';
// hooks
import useDidMountEffect from 'hooks/useDidMountEffect';
import useDataTable, { tableParamsSelector } from 'hooks/useDataTable';
import { useDropdownActions } from 'hooks/useDropdownContext';
// utils
import cn from 'classnames';
import { toastResponseErrors } from 'utils/responseErrorsHelper';
// regional date handling
import { getCurrentCountryCode } from 'selectors/regionalDateSelectors';
import { formatDateForUser } from 'utils/regionalDateHelper';
import { getCommitmentDirection } from 'utils/commitmentsHelper';
import { tableNames } from 'utils/constants';
import { allOrAnyLoadingsOptions, userFilterGroupNames, userFilterSectionTypes } from 'utils/constants/userFilters';
// api
import { acceptCommitment, fetchScheduledCommitments, applyCommitmentsFilter } from 'endpoints/commitments';
// styles
import styles from '../MySchedule.module.scss';

const formatDate = (date, countryCode) => formatDateForUser(date, countryCode);

const initialFilter = {
  sections: [{ type: 'PigMigration', condition: 'not_exist', key: 1, options: [] }],
  type: 'PigMigration',
};

const PigMovementsView = ({
  isOnline,
  isSchedulePageOutdated,
  openModal,
  router: { push },
  setTableParams,
  setIsSchedulePageOutdated,
  tableParams,
  tabs,
  createUserFilterAction,
  updateUserFilterAction,
  countryCode,
}) => {
  const { openDropdown } = useDropdownActions();
  const { formatMessage } = useIntl();
  const {
    data: { resources, isLoading, meta: { total } },
    fetchData,
    onPageChange,
    onPerPageChange,
    onUserFilterSectionsChange,
    onUserFilterSegmentSave,
    setDataByIndex,
  } = useDataTable(fetchScheduledCommitments, {
    setTableParams: (params) => setTableParams(tableNames.allCommitments, params),
    tableParams,
    filterFetcher: applyCommitmentsFilter,
    userFilterGroupName: userFilterGroupNames.loadCommitments,
    createUserFilter: createUserFilterAction,
    updateUserFilter: updateUserFilterAction,
  });
  // TODO: change this logic
  const isFiltersTouched = 'filters' in tableParams; // presence of 'filters' key means that there were manipulations with filters

  const sections = [
    { value: userFilterSectionTypes.load, label: formatMessage({ id: 'general.loadName' }) },
    { value: userFilterSectionTypes.loadType, label: formatMessage({ id: 'general.loadType' }) },
    { value: userFilterSectionTypes.arriveAt, label: formatMessage({ id: 'component.advancedSearchItem.arriveAt' }) },
    { value: userFilterSectionTypes.commitmentStatus, label: formatMessage({ id: 'general.confirmationStatus' }) },
    { value: userFilterSectionTypes.loadingStatus, label: formatMessage({ id: 'general.loadStatus' }) },
    { value: userFilterSectionTypes.pigMovements, label: formatMessage({ id: 'general.pigMovements' }) },
    { value: userFilterSectionTypes.salesResult, label: formatMessage({ id: 'general.salesResult' }) },
  ];

  useEffect(() => {
    // a flag latched while the Treatments tab was active is stale here: the mount fetch below
    // already loads fresh data, so just clear it instead of prompting for a refresh
    if (isSchedulePageOutdated) setIsSchedulePageOutdated(false);
  }, []);

  useEffect(() => {
    if (isOnline) {
      fetchData({ ...tableParams, filters: isFiltersTouched ? tableParams.filters : [initialFilter] })
        .catch(toastResponseErrors);
    }
  }, [isOnline]);

  useDidMountEffect(() => {
    if (isSchedulePageOutdated) {
      openModal(
        <OutdatedScheduleModal
          onLeave={() => setIsSchedulePageOutdated(false)}
          onRefresh={() => {
            fetchData({ ...tableParams, page: 1 }).then(() => {
              setIsSchedulePageOutdated(false);
            });
          }}
        />,
        { closeable: false }
      );
    }
  }, [isSchedulePageOutdated]);

  const handleGetOptions = (commitment, index) => {
    const { loading, status, id, acceptable, declinable } = commitment;
    const direction = getCommitmentDirection(commitment);
    const isCommitmentAccepted = status === 'accepted';
    const isCommitmentDeclined = status === 'declined';

    const acceptLoad = () => acceptCommitment(id)
      .then((resource) => {
        setDataByIndex(index, { ...resource, direction });
        openModal(<AcceptCommitmentModal loadName={loading.name} />);
      }).catch(toastResponseErrors);

    const declineLoad = () => {
      openModal(
        <DeclineCommitmentModal
          onSubmit={(resource) => setDataByIndex(index, { ...resource, ...direction })}
          commitment={commitment}
          load={loading}
        />
      );
    };

    return [
      { onClick: () => push(`/my-schedule/${commitment.id}`), light: true,
        label: <FormattedMessage id="general.button.reviewLoad" /> },
      { onClick: () => acceptLoad(loading), blue: true, label: <FormattedMessage id="general.confirm" />,
        hide: !acceptable || isCommitmentAccepted },
      { onClick: () => declineLoad(loading), red: true, label: <FormattedMessage id="general.reportConflict" />,
        hide: !declinable || isCommitmentDeclined },
    ];
  };

  return (
    <ScheduleViewShell
      allOrAnyConditionLabel={<FormattedMessage id="component.advancedSearchItem.allLoadsFilters" />}
      fetchData={fetchData}
      filterGroupName={userFilterGroupNames.loadCommitments}
      filterSections={sections}
      initialFilter={initialFilter}
      isFiltersTouched={isFiltersTouched}
      listOptions={allOrAnyLoadingsOptions}
      onUserFilterSectionsChange={onUserFilterSectionsChange}
      onUserFilterSegmentSave={onUserFilterSegmentSave}
      tableParams={tableParams}
    >
      <Panel className={styles['schedule-panel']}>
        <Panel.Heading
          title={<FormattedMessage id="general.pageTitle.mySchedule" />}
        >
          {tabs && <div className={styles['schedule-tabs']}>{tabs}</div>}
          <Button
            onClick={() => push('/my-schedule/archive')}
            className={cn('select-btn')}
            primary
          >
            <FormattedMessage id="general.archive" />
          </Button>
        </Panel.Heading>
        <Panel.Body className="ph-20">
          <Preloader isActive={isLoading} />
          {resources.map((commitment, idx) => {
            const isCommitmentPending = commitment.status === 'pending';
            const options = handleGetOptions(commitment, idx);
            const mobileOptions = [
              {
                onClick: () => push(`/my-schedule/${commitment.id}`),
                light: !isCommitmentPending,
                primary: isCommitmentPending,
                label: <FormattedMessage id="general.button.reviewLoad" />,
              }
            ];
            return (
              <Fragment key={commitment.id}>
                {(idx === 0 || (formatDate(commitment.arrive_at, countryCode) !==
                  formatDate(resources[idx - 1].arrive_at, countryCode))) && (
                  <DateLine date={formatDate(commitment.arrive_at, countryCode)} />
                )}
                <CommitmentCard
                  className="mv-10"
                  onDropdownOpen={(e) => openDropdown(e, options)}
                  commitment={commitment}
                  options={options}
                  mobileOptions={mobileOptions}
                  openModal={openModal}
                />
              </Fragment>
            );
          })}
          <NothingBox display={!resources.length} isLoading={isLoading}>
            <h2 className="lighter semibold">
              <FormattedMessage id="general.nothingBox.noLoads" />
            </h2>
          </NothingBox>
          {!!resources.length && (
            <PaginationWrapper
              onlyBottom
              totalItems={total}
              currentPage={tableParams.page}
              onPageChange={onPageChange}
              perPage={tableParams.per_page}
              onPerPageChange={onPerPageChange}
            />
          )}
        </Panel.Body>
      </Panel>
    </ScheduleViewShell>
  );

};

PigMovementsView.propTypes = {
  isOnline: T.bool.isRequired,
  isSchedulePageOutdated: T.bool.isRequired,
  router: T.object.isRequired,
  tableParams: T.object.isRequired,
  openModal: T.func.isRequired,
  setTableParams: T.func.isRequired,
  setIsSchedulePageOutdated: T.func.isRequired,
  tabs: T.node,
  createUserFilterAction: T.func.isRequired,
  updateUserFilterAction: T.func.isRequired,
  countryCode: T.string,
};

export default connect(
  (state) => ({
    tableParams: tableParamsSelector(state, tableNames.allCommitments),
    isOnline: state.network.isOnline,
    isSchedulePageOutdated: state.layout.isSchedulePageOutdated,
    countryCode: getCurrentCountryCode(state),
  }), {
    setIsSchedulePageOutdated,
    setTableParams,
    openModal,
    createUserFilterAction,
    updateUserFilterAction,
  }
)(PigMovementsView);
